import {
  ListResource,
  CreateResource,
  DeleteResource,
  GetResource,
  type ResourceConfig,
  UpdateResource
} from './resource-builder.model'

type Magic<T, A extends keyof T, B extends keyof NonNullable<T[A]> & string> = NonNullable<T[A]>[B]

type DTOTransformers<T extends ResourceConfig> = {
  list?(a: Magic<T, 'list', 'dtoResponse'>): Magic<T, 'list', 'response'>
  get?(a: Magic<T, 'get', 'dtoResponse'>): Magic<T, 'get', 'response'>
  create?: {
    request?(a: Magic<T, 'create', 'request'>): Magic<T, 'create', 'dtoRequest'>
    response?(a: Magic<T, 'create', 'dtoResponse'>): Magic<T, 'create', 'response'>
  }
  update?: {
    request?(a: Magic<T, 'update', 'request'>): Magic<T, 'update', 'dtoRequest'>
    response?(a: Magic<T, 'update', 'dtoResponse'>): Magic<T, 'update', 'response'>
  }
}

export class ResourceBuilder<T extends ResourceConfig> {
  private methods: {
    list: ListResource<Magic<T, 'list', 'request'>, Magic<T, 'list', 'response'>>
    get: GetResource<Magic<T, 'get', 'request'>, Magic<T, 'get', 'response'>>
    create: CreateResource<Magic<T, 'create', 'request'>, Magic<T, 'create', 'response'>>
    delete: DeleteResource<Magic<T, 'update', 'request'>, Magic<T, 'update', 'response'>>
    update: UpdateResource<Magic<T, 'update', 'request'>, Magic<T, 'update', 'response'>>
  }

  constructor(entityName: string, dtoTransformers?: DTOTransformers<T>) {
    this.methods = {
      list: new ListResource(entityName, dtoTransformers?.list),
      get: new GetResource(entityName, dtoTransformers?.get),
      create: new CreateResource(
        entityName,
        dtoTransformers?.create?.request,
        dtoTransformers?.create?.response
      ),
      update: new UpdateResource(
        entityName,
        dtoTransformers?.update?.request,
        dtoTransformers?.update?.response
      ),
      delete: new DeleteResource(entityName)
    }
  }

  async list(query?: Magic<T, 'list', 'request'>) {
    return this.methods.list.perform({
      payload: query
    })
  }

  async get(id: string | number, query?: Magic<T, 'get', 'request'>) {
    return this.methods.get.perform({
      urlParams: { id },
      payload: query
    })
  }

  async create(body: Magic<T, 'create', 'request'>) {
    return this.methods.create.perform({
      payload: body
    })
  }

  async update(id: string | number, body: Magic<T, 'update', 'request'>) {
    return this.methods.update.perform({
      urlParams: { id },
      payload: body
    })
  }

  async delete(id: string | number) {
    return this.methods.delete.perform({
      urlParams: { id }
    })
  }
}
